import { Injectable, OnDestroy } from "@angular/core";
import { Subject, BehaviorSubject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";
import { OrganizationCustomizationService } from "@onsip/common/services/api/resources/organizationCustomization/organization-customization.service";

const ONSIP_BLUE = "#165b8c";

interface TeamInfo {
  blurb: string;
  greeting: string;
  useCustomGreeting: boolean;
  organizationName: string;
  organizationId: string;
  logo: string;
  color: string;
  domain: string;
}

@Injectable({ providedIn: "root" })
export class TeamPageSettingsService implements OnDestroy {
  teamInfoSubject: BehaviorSubject<TeamInfo>;
  apiUser: any;
  private teamInfo: TeamInfo = {
    blurb: "",
    greeting: "",
    useCustomGreeting: false,
    organizationName: "",
    organizationId: "",
    logo: "",
    color: "",
    domain: ""
  };
  private doSubmit: Subject<any> = new Subject();
  private unsubscriber = new Subscription();

  constructor(
    private userService: UserService,
    private organizationCustomization: OrganizationCustomizationService
  ) {
    this.teamInfoSubject = new BehaviorSubject(this.teamInfo);
    this.init();
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  init(): void {
    this.unsubscriber.add(
      this.userService.selfUser.subscribe(apiUser => {
        this.apiUser = apiUser;
        this.teamInfo.organizationName = apiUser.contact.organization;
        this.teamInfo.organizationId = apiUser.organizationId;
        this.teamInfo.domain = apiUser.domain;
        this.setTeamInfoProperty([
          { property: "organizationName", value: this.teamInfo.organizationName },
          { property: "organizationId", value: this.teamInfo.organizationId },
          { property: "domain", value: this.teamInfo.domain }
        ]);
      })
    );

    this.unsubscriber.add(
      this.organizationCustomization.organization.subscribe(organizationCustomization => {
        const teamPageInfo = organizationCustomization.organizationInfo.teamPageInfo;
        if (teamPageInfo) {
          this.teamInfo.blurb = teamPageInfo.blurb;
          if (teamPageInfo.greeting) {
            this.teamInfo.greeting = teamPageInfo.greeting;
            this.teamInfo.useCustomGreeting = true;
          }
          this.teamInfo.color = teamPageInfo.accentColor || ONSIP_BLUE;
        }
        this.teamInfo.logo = organizationCustomization.organizationAvatarUrl;
        this.setTeamInfoProperty([
          { property: "blurb", value: this.teamInfo.blurb },
          { property: "greeting", value: this.teamInfo.greeting },
          { property: "useCustomGreeting", value: this.teamInfo.useCustomGreeting },
          { property: "color", value: this.teamInfo.color },
          { property: "logo", value: this.teamInfo.logo }
        ]);
      })
    );

    this.organizationCustomization.organizationCustomizationRead(); // skip onLogin

    this.doSubmit
      .pipe(debounceTime(100))
      .subscribe(params => this.organizationCustomization.organizationCustomizationEdit(params));
  }

  submit(): void {
    const params = {
      accentColor: this.teamInfo.color,
      blurb: this.teamInfo.blurb || "",
      greeting: this.teamInfo.greeting || ""
    };
    this.doSubmit.next(params);
  }

  setTeamInfoProperty(props: Array<{ property: keyof TeamInfo; value: string | boolean }>): void {
    props.forEach(
      <T extends keyof TeamInfo>({ property, value }: { property: T; value: TeamInfo[T] }) => {
        this.teamInfo[property] = value;
      }
    );
    this.teamInfoSubject.next(this.teamInfo);
  }
}
